import React, { useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import PageHeader from '../components/layout/PageHeader';
import { ShoppingBag, Tag, Palette, Ruler, Repeat } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import KitSwap, { KitPhoto } from '../components/kitswap/KitSwap';
import AskAboutThis from '../components/shop/AskAboutThis';
import { formatGBP } from '../lib/money';
import '../components/shop/ShopManager.css';
import './KitShopPage.css';

const categories = ['all', 'kit', 'training', 'casual', 'accessories'];
const categoryLabels = { all: 'All Products', kit: 'Match Kit', training: 'Training', casual: 'Casual Wear', accessories: 'Accessories' };
// B-49: blank price = "Price on request", never £0.00.
const priceText = (p) => formatGBP(p, 'Price on request');

/** The item's picture: its uploaded photo (B-49, v47 — fetched like Kit
 *  Swap's, see KitPhoto), else the old hosted-elsewhere image link, else the
 *  bag. */
const ProductImage = ({ product, accentColor, index = 0, iconSize = 40 }) => {
  const url = product.photo_urls?.[index];
  if (url) return <KitPhoto url={url} alt={product.name} className="product-photo" Placeholder={ShoppingBag} />;
  if (product.image_url && index === 0) return <img src={product.image_url} alt={product.name} />;
  return (
    <div className="product-placeholder">
      <ShoppingBag size={iconSize} style={{ color: accentColor, opacity: 0.4 }} />
    </div>
  );
};

const KitShopPage = () => {
  const { clubSlug } = useParams();
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [asking, setAsking] = useState(false); // B-49: the "Ask about this" form is open
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, getMembership } = useAuth();

  const { data: club } = useQuery({ queryKey: ['club', clubSlug], queryFn: () => axios.get(`/clubs/${clubSlug}`).then(r => r.data) });
  const { data: products = [], isLoading } = useQuery({ queryKey: ['products', clubSlug], queryFn: () => axios.get(`/clubs/${clubSlug}/products`).then(r => r.data) });

  const filtered = activeCategory === 'all' ? products : products.filter(p => p.category === activeCategory);
  // B-49: "Ask about this" only when the club has set a kit-contact email,
  // and never for sponsor accounts (business logins, not shoppers).
  const membership = user ? getMembership(clubSlug) : null;
  const canAsk = !!club?.shop_enquiries_on && (!membership || membership.role !== 'sponsor');
  const openProduct = (product, ask = false) => { setSelectedProduct(product); setPhotoIndex(0); setAsking(ask && !!membership); };
  const closeProduct = () => { setSelectedProduct(null); setAsking(false); };
  const accentColor = club?.accent_color || '#C4943A';

  // B-45: Shop = two tabs, Kit Shop | Kit Swap. Kit Swap only exists when the
  // club has switched it on, and only for signed-in people in the club (the
  // API is members-only; visitors keep the plain Kit Shop).
  const swapAvailable = !!club?.kit_swap_on && !!membership && membership.role !== 'sponsor';
  const tab = swapAvailable && searchParams.get('tab') === 'swap' ? 'swap' : 'shop';
  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === 'swap') next.set('tab', 'swap'); else next.delete('tab');
    next.delete('view');
    next.delete('chat');
    next.delete('item');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="shop-page" style={{ '--accent': accentColor }}>
      <Navbar club={club} />

      <PageHeader
        icon={tab === 'swap' ? Repeat : ShoppingBag}
        title="Shop"
        subtitle={tab === 'swap'
          ? <>{club?.name} — free kit passed on between members</>
          : <>{club?.name} — Official Marakin Sportswear</>}
      />

      <div className="shop-content">
        {swapAvailable && (
          <div className="shop-tabs" role="tablist" data-tour="shop-tabs">
            <button type="button" role="tab" aria-selected={tab === 'shop'} className={`shop-tab ${tab === 'shop' ? 'active' : ''}`} onClick={() => setTab('shop')}>Kit Shop</button>
            <button type="button" role="tab" aria-selected={tab === 'swap'} className={`shop-tab ${tab === 'swap' ? 'active' : ''}`} onClick={() => setTab('swap')}>Kit Swap</button>
          </div>
        )}

        {tab === 'swap' && (
          <KitSwap key={clubSlug} clubSlug={clubSlug} initialView={searchParams.get('view') === 'mine' ? 'mine' : 'browse'}
            initialChatId={searchParams.get('chat') || null} initialItemId={searchParams.get('item') || null} />
        )}

        {tab === 'shop' && (<>
        {/* Category filter */}
        <div className="pill-group">
          {categories.map(c => (
            <button key={c} className={`pill-btn ${activeCategory === c ? 'active' : ''}`}
              onClick={() => setActiveCategory(c)}>
              {categoryLabels[c]}
            </button>
          ))}
        </div>

        {isLoading && <div className="page-loading"><div className="spinner" /></div>}

        {!isLoading && filtered.length === 0 && (
          <div className="empty-state">No products in this category yet.</div>
        )}

        {/* Product grid */}
        <div className="products-grid">
          {filtered.map(product => (
            <div key={product.id} className="product-card card" onClick={() => openProduct(product)}>
              <div className="product-image" style={{ background: `linear-gradient(135deg, ${accentColor}20, ${accentColor}05)` }}>
                <ProductImage product={product} accentColor={accentColor} />
                <span className="product-category-badge badge badge-gold">
                  {categoryLabels[product.category]}
                </span>
              </div>
              <div className="product-info">
                <h3>{product.name}</h3>
                <p className="product-desc">{product.description}</p>
                <div className="product-meta">
                  <div className="product-price">
                    <Tag size={14} style={{ color: accentColor }} />
                    {priceText(product.price)}
                  </div>
                  {product.is_customisable && (
                    <span className="badge badge-blue">Customisable</span>
                  )}
                </div>
                {product.colours?.length > 0 && (
                  <div className="product-colours">
                    <Palette size={13} style={{ color: 'var(--text-muted)' }} />
                    <span>{product.colours.join(', ')}</span>
                  </div>
                )}
              </div>
              <button type="button" className="btn btn-primary product-cta"
                onClick={(e) => { e.stopPropagation(); openProduct(product, canAsk); }}>
                {canAsk ? 'Ask about this' : 'See details'}
              </button>
            </div>
          ))}
        </div>
        </>)}
      </div>

      {/* Product detail modal */}
      {selectedProduct && (
        <div className="modal-overlay" onClick={closeProduct}>
          <div className="modal-card product-modal" onClick={e => e.stopPropagation()}>
            {selectedProduct.photo_urls?.length > 0 ? (
              <div className="shop-photo-gallery">
                <ProductImage product={selectedProduct} accentColor={accentColor} index={Math.min(photoIndex, selectedProduct.photo_urls.length - 1)} />
                {selectedProduct.photo_urls.length > 1 && (
                  <div className="shop-photo-dots">
                    {selectedProduct.photo_urls.map((u, i) => (
                      <button type="button" key={u} className={`shop-photo-dot ${i === photoIndex ? 'active' : ''}`}
                        aria-label={`Photo ${i + 1} of ${selectedProduct.photo_urls.length}`} aria-pressed={i === photoIndex}
                        onClick={() => setPhotoIndex(i)}>
                        <KitPhoto url={u} alt="" Placeholder={ShoppingBag} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="product-modal-image" style={{ background: `linear-gradient(135deg, ${accentColor}20, ${accentColor}05)` }}>
                {selectedProduct.image_url
                  ? <img src={selectedProduct.image_url} alt={selectedProduct.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <ShoppingBag size={60} style={{ color: accentColor, opacity: 0.35 }} />}
              </div>
            )}
            <div className="product-modal-body">
              <div className="product-modal-header">
                <div>
                  <span className="badge badge-gold" style={{ marginBottom: 8, display: 'inline-flex' }}>
                    {categoryLabels[selectedProduct.category]}
                  </span>
                  <h2>{selectedProduct.name}</h2>
                  <div className="product-modal-price">{priceText(selectedProduct.price)}</div>
                </div>
              </div>
              {selectedProduct.description && <p className="product-modal-desc">{selectedProduct.description}</p>}

              {selectedProduct.sizes?.length > 0 && (
                <div className="product-modal-section">
                  <label className="label"><Ruler size={13} /> Available sizes</label>
                  <div className="size-pills">
                    {selectedProduct.sizes.map(s => <span key={s} className="size-pill">{s}</span>)}
                  </div>
                </div>
              )}
              {selectedProduct.colours?.length > 0 && (
                <div className="product-modal-section">
                  <label className="label"><Palette size={13} /> Colours</label>
                  <div className="size-pills">
                    {selectedProduct.colours.map(c => <span key={c} className="size-pill">{c}</span>)}
                  </div>
                </div>
              )}

              {/* B-49: the dead "Contact kit manager" button is now "Ask about
                  this" — an in-app enquiry emailed to the club's kit contact.
                  Hidden when the club hasn't set one. */}
              <div className="product-modal-cta">
                {!canAsk ? (
                  <p className="cta-note">To order, speak to someone at the club.{selectedProduct.is_customisable ? ' This item can be personalised.' : ''}</p>
                ) : !user ? (
                  <>
                    <p className="cta-note">Sign in to ask the club about this item.</p>
                    <Link to="/login" state={{ from: `/${clubSlug}/shop` }} className="btn btn-primary"
                      style={{ width: '100%', justifyContent: 'center' }}>
                      Sign in to ask
                    </Link>
                  </>
                ) : !membership ? (
                  <>
                    <p className="cta-note">Asking about items is for members of {club?.name || 'the club'}.</p>
                    <Link to={`/${clubSlug}`} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
                      Join {club?.name || 'the club'} to ask about items
                    </Link>
                  </>
                ) : asking ? (
                  <AskAboutThis key={selectedProduct.id} clubSlug={clubSlug} clubName={club?.name} clubVerified={!!club?.verified_at} product={selectedProduct} onCancel={() => setAsking(false)} />
                ) : (
                  <>
                    <p className="cta-note">Ask the club's kit contact about sizes, ordering or collection.{selectedProduct.is_customisable ? ' This item can be personalised.' : ''}</p>
                    <button type="button" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
                      onClick={() => setAsking(true)}>
                      Ask about this
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KitShopPage;
